import {
  DayOneVerificationResponseSchema,
  GestureItem,
  VerificationType,
} from "@amzn/client-workbench-api-model";
import {
  Instructions,
  VideoOverlay,
} from "@amzn/itsvideo-recorder/lib/VideoRecorder";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";

import {
  cachePreSignedUrlsTtl,
  createVerification,
  getPreSignedUrlsTtlFromCache,
  VerificationStatus,
} from "@/apis/IdentityCheckHelper";
import { updateOnboardingStep } from "@/apis/UserOnboardingStepHelper";
import DosDonts from "@/components//IdentityCheck/DosDonts";
import ErrorBanner from "@/components/Commons/ErrorBanner";
import VideoRecorderCustom from "@/components/Commons/VideoRecorderCustom";
import { MediaCheckWrapper } from "@/components/Commons/VideoRecorderCustom/MediaCheckWrapper";
import { STEP_ID } from "@/components/constants";
import { ChatWithITSupportFlyoutLink } from "@/components/GetSupport";
import ChallengeActions from "@/components/IdentityCheck/ChallengeActions";
import { getRetryDelayMs, shouldRetry } from "@/helpers/react-query-helper";
import { toVerificationType } from "@/helpers/verification-status-helper";
import { AppContext } from "@/stores/appStore";
import { ActionType } from "@/stores/constants";
import { useFeatureFlag } from "@/stores/contexts/FeatureFlagsContext";
import {
  Day1EventAction,
  Day1EventCategory,
  Day1EventLabel,
} from "@/types/snowplow-events";

import { StepBodyStyledText } from "../Commons/StepBodyStyledText";
import {
  CompletedGesturesOverlay,
  gestureOverlay,
} from "./ManualVerificationOverlays";

export const TEN_MINS_IN_MILLIS = 600_000;
const INSTRUCTION_INTERVAL_MS = 13_000;

const MP4_EXTENSION = ".mp4";

/**
 * Derive the GIF URL for a gesture from its backend video assetUrl. The GIF assets are uploaded to the
 * same gestures-<stage> location under the same name as the MP4 demo videos, differing only in extension,
 * so the GIF URL is the video URL with its .mp4 extension swapped for .gif. The gesture catalog only ever
 * serves plain `.mp4` CloudFront URLs (no query string), so anything else returns undefined and the caller
 * falls back to no inline GIF.
 */
export const toGestureGifUrl = (assetUrl: string): string | undefined =>
  assetUrl.toLowerCase().endsWith(MP4_EXTENSION)
    ? `${assetUrl.slice(0, -MP4_EXTENSION.length)}.gif`
    : undefined;

interface LightweightIdVerificationProps {
  /*** The Snowplow event category ***/
  eventCategory:
    | Day1EventCategory.IDCheckFullChallenge
    | Day1EventCategory.IDCheckLight;
}

export const DATA_TEST_ID_LIGHTWEIGHT_ID_VERIFICATION =
  "LightweightIdVerificationView";
/**
 * A view that presents challenge actions only which
 * @constructor
 */
export default function LightweightIdVerification({
  eventCategory,
}: LightweightIdVerificationProps): JSX.Element {
  const [gestureItems, setGestureItems] = useState<GestureItem[]>([]);
  const [instructions, setInstructions] = useState<Instructions[]>([]);
  const [gestureChallengeHidden, setGestureChallengeHidden] = useState(true);
  const [gestureChallengeEndTime, setGestureChallengeEndTime] = useState(0);
  const [gestureChallengeExpired, setGestureChallengeExpired] = useState(false);
  const recordingTimeLimitMs = 23_000;
  const countDownTimeMs = 0;
  const abortController = new AbortController();
  const currentTime: number = new Date().getTime();
  const intl = useIntl();
  const { dispatch, state } = useContext(AppContext);
  const verificationType: VerificationType = toVerificationType(
    state.docsVerificationStatus
  );
  // India-scoped gate, read once and baked into the instructions' gifUrl at fetch time (see onSuccess).
  const animatedGifGesturesEnabled = useFeatureFlag("animatedGifGestures");
  // Derive whether GIFs are showing from the already-baked instructions rather than re-reading the flag,
  // so the recorder's GIFs and the ChallengeActions fallback always agree even if the flag resolves after
  // the gesture fetch (no divergence window where neither the GIFs nor the demo videos render).
  const showingGifGestures = instructions.some((step) => Boolean(step.gifUrl));

  /**
   * Set the refPage to provide context to IT Support should the new hire initiate
   * a chat with support
   */
  useEffect(() => {
    updateOnboardingStep("D1_LOGIN");
    const refPage =
      eventCategory === Day1EventCategory.IDCheckLight
        ? "ezo-identity-check-manual-lightweight-challenge-unable-to-complete-challenge"
        : "ezo-identity-check-manual-full-challenge-unable-to-complete-challenge";
    dispatch({
      type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
      chatSupportRefPage: refPage,
    });
  }, []);

  useEffect(() => {
    // Next is hidden by default, however when RIV is selected, it should be enabled again.
    dispatch({
      type: ActionType.SET_HIDE_NEXT,
      hideNext: false,
      stepId: STEP_ID.IDENTITY_CHECK,
    });
    // Determine the type of RIV this view is rendered by. If <FullIdVerification /> mounts <LightweightIdVerification />,
    // <FullIdVerification /> component updates the context, but otherwise the below code runs to set context.
    // state.docsVerificationStatus is set initially in src/hooks/useUserProgress.ts onSuccess handler.
    if (state.docsVerificationStatus === VerificationStatus.LIGHT) {
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: "ON_LIGHT_RIV_CHALLENGE_STEP",
      });
    }

    // Track journey metrics for when this section is toggled open. FRIV submits the section toggled metric
    //  through <FullIdVerification /> so don't track a metric if this component was mounted by FRIV.
    if (eventCategory === Day1EventCategory.IDCheckLight) {
      trackStructEvent({
        category: Day1EventCategory.IDCheckLight,
        action: Day1EventAction.SectionToggled,
      });
    }
  }, []);

  /**
   * Creates a chat support link with metrics props set up.
   */
  const ITSupportLinkWithMetrics: JSX.Element = (
    <ChatWithITSupportFlyoutLink
      localeMessageId="text-chatWithITSupport"
      reason="verificationWithChime"
      metricProps={{
        // Currently, the only eventCategories in use are Day1EventCategory.IDCheckLight and Day1EventCategory.IDCheckFullChallenge.
        // Change this logic later if LightweightIdVerification is used by others.
        category: eventCategory,
        action: Day1EventAction.ChatWithSupport,
        label: Day1EventLabel.UnableToCompleteChallenge,
      }}
    />
  );

  /**
   * Fetches the S3 pre-signed urls and gestures which NHs will be asked to complete within a certain time frame.
   */
  const {
    mutate: startVerification,
    isLoading: isCreateVerificationLoading,
    isError: isCreateVerificationError,
  } = useMutation(
    (): Promise<DayOneVerificationResponseSchema> => {
      return createVerification(
        verificationType,
        abortController.signal,
        state.requiredDocsForHiringCountry >= 2
          ? {
              primary: {
                country: state.primaryIdSelection?.issuingCountry ?? "",
                formType: state.primaryIdSelection?.documentType ?? "",
              },
              secondary: {
                // This will either be the string "secondary ID provided" or undefined
                formType: state.secondaryIdProvided,
              },
            }
          : {
              primary: {
                country: state.primaryIdSelection?.issuingCountry ?? "",
                formType: state.primaryIdSelection?.documentType ?? "",
              },
            },
        state.selectedLanguage
      );
    },
    {
      onSuccess: (data: DayOneVerificationResponseSchema) => {
        // Track journey metrics for when challenge actions are retrieved. At time of writing, only 2 gestures are
        //  returned and this will need to be updated if more gestures are retrieved in the future.
        data.gestures.forEach((gesture, index) => {
          trackStructEvent({
            category: eventCategory,
            action:
              index === 0
                ? Day1EventAction.ChallengeAction1Set
                : Day1EventAction.ChallengeAction2Set,
            label: gesture.key,
          });
        });

        const instructions: Instructions[] = data.gestures.map(
          (gesture, index) => ({
            text: gesture.displayText,
            displayTimestampMs: index * INSTRUCTION_INTERVAL_MS,
            bannerBackGroundColor: index > 0 ? "pink70" : "primary80",
            // Attach the gesture's GIF URL (derived from the backend video assetUrl) only when the flag
            // is on, so the recorder shows it inline in step with the active instruction.
            gifUrl: animatedGifGesturesEnabled
              ? toGestureGifUrl(gesture.assetUrl)
              : undefined,
          })
        );
        setInstructions(instructions);
        setGestureItems(data.gestures);
        setGestureChallengeHidden(false);
        const ttl =
          getPreSignedUrlsTtlFromCache() ||
          new Date().getTime() + TEN_MINS_IN_MILLIS;
        setGestureChallengeEndTime(ttl);

        // Store the TTL in cache
        cachePreSignedUrlsTtl(ttl);
      },
      retry: shouldRetry,
      retryDelay: getRetryDelayMs,
    }
  );

  /**
   * Decide if we should render the time expired view to NHs by checking the expiration time from the cache or local
   * state.
   */
  useEffect(() => {
    // Retrieve from cache first before checking the local state
    const endTime = getPreSignedUrlsTtlFromCache() ?? gestureChallengeEndTime;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (endTime > 0) {
      if (endTime < currentTime) {
        // Passed the existing expiration time
        setGestureChallengeExpired(true);
      } else {
        // Set the expiration time in the future.
        timeout = setTimeout(() => {
          setGestureChallengeExpired(true);
        }, TEN_MINS_IN_MILLIS);
      }
    }

    if (timeout) {
      return () => clearTimeout(timeout as ReturnType<typeof setTimeout>);
    }
  }, [gestureChallengeEndTime, currentTime]);
  const hasUpdatedStep = useRef(false);
  const startGestureChallenges = () => {
    if (!hasUpdatedStep.current) {
      updateOnboardingStep("D1_IDV_START");
      hasUpdatedStep.current = true;
    }

    // TODO: Do we need to keep external progress associated to the caller's ID to ensure they don't just refresh the page?
    startVerification();
  };

  const videoOverlays = instructions
    .map((instruction, i) => [
      {
        component: gestureOverlay(instruction),
        timeToShowMs: instruction.displayTimestampMs,
        timeToHideMs: instruction.displayTimestampMs + 5_000,
        transitionTimeMs: 1000,
      },
      {
        component: CompletedGesturesOverlay[i],
        timeToShowMs: instruction.displayTimestampMs + 10_000,
        timeToHideMs: instruction.displayTimestampMs + 13_000,
        transitionTimeMs: 500,
      },
    ])
    .flat() satisfies VideoOverlay[];

  /* eslint-disable @typescript-eslint/no-unsafe-assignment */
  return (
    <div data-test-id={DATA_TEST_ID_LIGHTWEIGHT_ID_VERIFICATION}>
      {gestureChallengeExpired && (
        <ErrorBanner>
          <FormattedMessage
            id="identityCheck-challenges-expired"
            values={{
              chatWithIt: ITSupportLinkWithMetrics,
              b: (chunks: string) => <b>{chunks}</b>,
            }}
          />
        </ErrorBanner>
      )}
      {!gestureChallengeExpired && (
        <Col rowGap="S400">
          <StepBodyStyledText>
            <Text fontWeight="regular">
              <FormattedMessage id="identityCheck-expander-body-intro" />
            </Text>
            <Text
              fontWeight="regular"
              dataTestId="lightweightInstructionDataTestId"
            >
              <FormattedMessage
                id="identityCheck-expander-body-light-instruction"
                values={{ b: (chunks: string) => <b>{chunks}</b> }}
              />
            </Text>
            <Text fontWeight="regular">
              <FormattedMessage
                id="identityCheck-expander-body-helpLinkPre"
                values={{
                  chatWithITSupportLink: ITSupportLinkWithMetrics,
                }}
              />
            </Text>
          </StepBodyStyledText>
          {isCreateVerificationLoading && <Spinner showText />}
          {!isCreateVerificationLoading && gestureChallengeHidden && (
            <Row gridGap="S300">
              <Button
                onClick={startGestureChallenges}
                variant={ButtonVariant.Primary}
                dataTestId="unhideChallenge"
              >
                <FormattedMessage id="identityCheck-challenges-instructions-unhideButton" />
              </Button>
              {isCreateVerificationError && (
                <MessageBanner type={MessageBannerType.Error}>
                  <Text
                    fontWeight="regular"
                    dataTestId="errorMessageBannerDataTestId"
                  >
                    <FormattedMessage
                      id="error-somethingWentWrong"
                      values={{
                        chatWithITSupportLink: ITSupportLinkWithMetrics,
                      }}
                    />
                  </Text>
                </MessageBanner>
              )}
            </Row>
          )}

          {!gestureChallengeHidden && (
            <Fragment>
              <Col rowGap="S300">
                <Hr size="narrow" color="neutral10" />
                <StepBodyStyledText>
                  <Text fontWeight="regular">
                    <FormattedMessage
                      id="identityCheck-challenges-instructions-completeByTime"
                      values={{
                        time: new Date(gestureChallengeEndTime).toLocaleString(
                          intl.locale,
                          { hour: "numeric", minute: "numeric", hour12: true }
                        ),
                      }}
                    />
                  </Text>
                </StepBodyStyledText>

                <StepBodyStyledText>
                  <Text
                    fontWeight="medium"
                    dataTestId="gestureChallengesDataTestId"
                  >
                    <FormattedMessage id="identityCheck-challenges-instructions" />
                  </Text>
                </StepBodyStyledText>
                {/* Hide the static demo videos when inline GIF gestures are showing. */}
                {!showingGifGestures && (
                  <ChallengeActions gestureItems={gestureItems} />
                )}
              </Col>
              <DosDonts />
              <MediaCheckWrapper>
                <VideoRecorderCustom
                  recordCompletedI18nId="identityCheck-challenges-completed"
                  instructions={instructions}
                  documentType="LIVENESS_RECORDING"
                  recordingTimeLimitMs={recordingTimeLimitMs}
                  countDownTimeMs={countDownTimeMs}
                  eventCategory={eventCategory}
                  refPage="ezo-identity-check-manual-lightweight"
                  showProgressBar={true}
                  overlays={videoOverlays}
                />
              </MediaCheckWrapper>
            </Fragment>
          )}
        </Col>
      )}
    </div>
  );
}
